import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import SafeImage from "../components/site/SafeImage";
import { CLINIC, waLink } from "../lib/clinic";
import { CLINIC_PHOTOS } from "../lib/clinicPhotos";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph } from "../lib/schema";

const insurers = [
  "Bajaj Allianz",
  "SBI General",
  "Go Digit",
  "Manipal Cigna",
  "Reliance General",
  "Universal Sompo",
  "HDFC ERGO",
  "TATA AIG",
  "Care Health",
  "Future Generali",
  "Acko",
  "Navi",
  "Galaxy Health",
  "Safeway",
];

const tpas = [
  "Medi Assist",
  "Link-K Insurance TPA",
  "East West Assist (EWA)",
  "AKNA Health Insurance TPA",
  "FHPL",
  "Heritage Health",
  "Health India Insurance TPA",
];

const Insurance = () => {
  usePageMeta({
    title: "Insurance & Cashless Mediclaim | Shantanu Netralaya, Varanasi",
    path: "/insurance",
    description: "Cashless treatment at Shantanu Netralaya, Varanasi — empanelled with Bajaj Allianz, SBI General, HDFC ERGO, Manipal Cigna, TATA AIG, Medi Assist, Ayushman Bharat PM-JAY and 20+ more insurers.",
  });
  usePageJsonLd(graph([breadcrumb([{ name: "Home", path: "/" }, { name: "Insurance", path: "/insurance" }])]));
  return (
    <div data-testid="insurance-page">
      <section className="pt-20 pb-10 container-page">
        <div className="overline mb-4">Insurance & Cashless</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Quality eye care — covered by your insurance.
        </h1>
        <p className="mt-5 max-w-2xl prose-elegant">
          Shantanu Netralaya Eye Hospital is empanelled with all leading private insurers, TPAs
          and the <span className="font-medium">Ayushman Bharat PM-JAY</span> government scheme.
          Cashless treatment is available for eligible card-holders — bring your policy card, a
          photo ID and any recent reports.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <a href={waLink("Hi, I would like to check if my insurance is accepted at Shantanu Netralaya.")} target="_blank" rel="nofollow noopener noreferrer" className="btn-primary" data-testid="insurance-whatsapp-btn">
            <MessageCircle size={16} /> Check my insurance on WhatsApp
          </a>
          <a href={`tel:${CLINIC.phoneRaw}`} className="btn-ghost" data-testid="insurance-call-btn">
            <Phone size={14} /> Call {CLINIC.phone}
          </a>
        </div>
      </section>

      {/* Empanelment posters */}
      <section className="py-8 container-page grid md:grid-cols-2 gap-8" data-testid="insurance-posters">
        <figure className="rounded-2xl overflow-hidden border border-border bg-white">
          <SafeImage
            src={CLINIC_PHOTOS.insurancePoster}
            alt="Shantanu Netralaya — Cashless mediclaim facilities available for card holders"
            label="Cashless Mediclaim"
            className="w-full aspect-[4/5]"
          />
        </figure>
        <figure className="rounded-2xl overflow-hidden border border-border bg-white">
          <SafeImage
            src={CLINIC_PHOTOS.ayushmanBharat}
            alt="Shantanu Netralaya empanelled under Ayushman Bharat (PM-JAY) — cashless treatment for eligible beneficiaries"
            label="Ayushman Bharat PM-JAY"
            className="w-full aspect-[4/5]"
          />
        </figure>
      </section>

      {/* Insurer + TPA list */}
      <section className="py-20 container-page grid md:grid-cols-2 gap-12" data-testid="insurance-lists">
        <div>
          <div className="overline mb-3">Private Insurers</div>
          <h2 className="font-serif text-3xl">Empanelled insurance companies</h2>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {insurers.map((n) => (
              <li key={n} className="flex items-center gap-2 text-sm">
                <CheckCircle2 size={14} strokeWidth={1.75} className="text-[hsl(var(--accent))]" />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="overline mb-3">Third Party Administrators</div>
          <h2 className="font-serif text-3xl">Empanelled TPAs</h2>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {tpas.map((n) => (
              <li key={n} className="flex items-center gap-2 text-sm">
                <CheckCircle2 size={14} strokeWidth={1.75} className="text-[hsl(var(--accent))]" />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Ayushman & how it works */}
      <section className="py-20 border-y border-border bg-[hsl(var(--secondary))]/40">
        <div className="container-page grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <ShieldCheck size={30} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
            <h3 className="font-serif text-3xl mt-4">Ayushman Bharat (PM-JAY)</h3>
            <p className="mt-4 prose-elegant">
              We are proud to be empanelled under the <span className="font-medium">
              Pradhan Mantri Jan Arogya Yojana (PM-JAY)</span>. Eligible beneficiaries receive
              <span className="font-medium"> cashless treatment</span> for cataract surgery and
              other covered eye procedures — with the same premium care, technology and
              consultation we offer every patient.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex gap-2 items-start"><CheckCircle2 size={16} className="text-[hsl(var(--accent))] mt-0.5" />Cashless & seamless treatment for eligible card-holders</li>
              <li className="flex gap-2 items-start"><CheckCircle2 size={16} className="text-[hsl(var(--accent))] mt-0.5" />High-quality care from specialist ophthalmologists</li>
              <li className="flex gap-2 items-start"><CheckCircle2 size={16} className="text-[hsl(var(--accent))] mt-0.5" />Modern technology & advanced diagnostic facilities</li>
              <li className="flex gap-2 items-start"><CheckCircle2 size={16} className="text-[hsl(var(--accent))] mt-0.5" />Compassionate, patient-centred approach</li>
            </ul>
          </div>
          <div>
            <ShieldCheck size={30} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
            <h3 className="font-serif text-3xl mt-4">How cashless works</h3>
            <ol className="mt-6 space-y-4 text-sm">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-white text-xs flex items-center justify-center shrink-0">1</span>
                <span><span className="font-medium">Confirm coverage:</span> WhatsApp us your policy details before the visit and we'll verify empanelment & benefit limits.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-white text-xs flex items-center justify-center shrink-0">2</span>
                <span><span className="font-medium">Bring documents:</span> Policy / Ayushman card, government photo ID, and any previous eye reports.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-white text-xs flex items-center justify-center shrink-0">3</span>
                <span><span className="font-medium">Pre-authorisation:</span> Our TPA desk raises the pre-auth with your insurer / PM-JAY portal — usually cleared within a few hours.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-white text-xs flex items-center justify-center shrink-0">4</span>
                <span><span className="font-medium">Surgery day:</span> You focus on getting better. We handle the paperwork with the insurer directly.</span>
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 text-center container-page">
        <h3 className="font-serif text-3xl">Not sure if your insurance is accepted?</h3>
        <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
          Send us your policy details on WhatsApp and we'll confirm eligibility within minutes.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-4">
          <a href={waLink()} target="_blank" rel="nofollow noopener noreferrer" className="btn-primary" data-testid="insurance-cta-whatsapp">
            <MessageCircle size={16} /> Ask on WhatsApp
          </a>
          <Link to="/appointment" className="btn-ghost" data-testid="insurance-cta-appointment">Book Appointment</Link>
        </div>
      </section>
    </div>
  );
};

export default Insurance;
